import React, { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { TextField } from '@mui/material';
import { IAddMoreCreatorsModal } from '../dto/IAddMoreCreatorsModal';
import { useTranslation } from 'react-i18next';
import { emailRegex } from '../../../../shared/functions/Functions';
import { ErrorToast, SuccessToast, WarningToast } from '../../../../utils/toasters';
import { AgencyService } from '../../service';
import { IRegisterAgencyInvite } from '../dto/IRegisterAgency';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../store/rootReducer';
import Chip from '../../../../shared/components/chip/Chip';

export function AddMoreCreatorsModal({ show, closeModal, setUpdate }: IAddMoreCreatorsModal) {
    const { t } = useTranslation();
    const { user } = useSelector((reduxState: IAllStates) => reduxState.auth);
    const [emailInput, setEmailInput] = useState('');
    const [inviteUsers, setInviteUsers] = useState<IRegisterAgencyInvite>({
        emailsArray: [],
        userId: user.id,
    });
    const inviteUsersRequest = () => {
        AgencyService.inviteUsers(inviteUsers)
            .then(() => {
                SuccessToast(t('brand.campaign.book.invite.successfully_invite_user'));

                setInviteUsers({
                    ...inviteUsers,
                    emailsArray: [],
                });

                setEmailInput('');
                closeModal();

                if (setUpdate) {
                    setUpdate(prevState => !prevState);
                }
            })
            .catch(err => ErrorToast(err));
    };
    const addEmail = () => {
        const emails = emailInput
            .split(',')
            .map(email => email.trim())
            .filter(Boolean);
        if (!emails.length) {
            return;
        }
        const invalidEmails = emails.filter(email => !emailRegex.test(email));
        if (invalidEmails.length) {
            return WarningToast(t('general.toasts.warnValidEmail'));
        }
        const uniqueEmails = emails.filter(email => !inviteUsers.emailsArray.includes(email));
        if (!uniqueEmails.length) {
            return WarningToast('All emails are already added');
        }
        setInviteUsers(prev => ({
            ...prev,
            emailsArray: [...prev.emailsArray, ...uniqueEmails],
        }));
        setEmailInput('');
    };
    const removeEmail = (email: string) => {
        setInviteUsers(prev => ({
            ...prev,
            emailsArray: prev.emailsArray.filter(item => item !== email),
        }));
    };
    const handleCloseModal = () => {
        setInviteUsers(prev => ({
            ...prev,
            emailsArray: [],
        }));

        setEmailInput('');
        closeModal();
    };

    return (
        <Modal show={show} onHide={handleCloseModal} className="modal-dark invite-creators-modal" size="sm" centered>
            <Modal.Header className="w-100 justify-content-center border-bottom">
                <div className="d-flex flex-column justify-content-center align-items-center text-center">
                    {/* <h4 className="mb-1">{t('agency.incite_creators')}</h4>
                    <p className="font-14 mb-0">{t('agency.incite_creators_desc')}</p> */}
                    <h4 className="mb-1">{t('agency.invite_creators')}</h4>
                    <p className="font-14 mb-0">{t('agency.invite_creators_desc')}</p>
                </div>
            </Modal.Header>
            <Modal.Body>
                <h5 className="mb-2 ms-2">{t('agency.email')}</h5>
                <div className="my-1 d-flex align-items-center ">
                    <TextField
                        label="E-mail *"
                        variant="outlined"
                        value={emailInput}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailInput(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                addEmail();
                            }
                        }}
                        size="small"
                        className="w-100"
                    />
                    <Button
                        variant="outline-primary"
                        className="ms-2 d-flex align-items-center add-email-btn"
                        onClick={addEmail}
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ marginRight: 6 }}
                        >
                            <path d="M15 9.75H9.75V15H8.25V9.75H3V8.25H8.25V3H9.75V8.25H15V9.75Z" fill="currentColor" />
                        </svg>
                        {t('general.button.add')}
                    </Button>
                </div>
                <p className="font-13" style={{ color: '#9EA5AB' }}>
                    {t('agency.add_a_comma')}
                </p>

                <div className="d-flex flex-wrap gap-2 mb-3">
                    {inviteUsers.emailsArray.map(email => (
                        <Chip key={email} label={email} onDelete={() => removeEmail(email)} />
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-between border-top">
                <Button onClick={handleCloseModal} variant="outline-primary" className="btn-sm mx-2 ">
                    {t('general.button.cancel')}
                </Button>
                <Button
                    disabled={!inviteUsers?.emailsArray?.length}
                    className="btn-sm text-uppercase"
                    onClick={inviteUsersRequest}
                >
                    {t('agency.send_invite')} ({inviteUsers?.emailsArray?.length})
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
